import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import {
  DataContextBuilder,
  ElderNumberCellDirective,
  ElderQuantityPipe,
  ElderTableComponent,
  ElderTranslatePipe,
  ElderTranslateService,
  FilterUtil,
  IDataContext,
  Interval,
  IsoIntervalPipe,
  LocalListDataSource,
  Sort,
  SortUtil,
  Unit,
} from '@elderbyte/ngx-starter'
import { Observable } from 'rxjs'
import { MexusViewMode } from '../../../../context/view-mode-context/mexus-view-mode'
import { ViewModeContextService } from '../../../../context/view-mode-context/view-mode-context.service'
import { WCOrderAggregate } from '../../work-center-utilisation'

@Component({
  selector: 'app-work-center-orders',
  templateUrl: './work-center-orders.component.html',
  styleUrls: ['./work-center-orders.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    ElderNumberCellDirective,
    AsyncPipe,
    ElderTranslatePipe,
    IsoIntervalPipe,
    ElderQuantityPipe,
  ],
})
export class WorkCenterOrdersComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  public readonly Interval = Interval
  public readonly MexusViewMode = MexusViewMode
  public readonly data: IDataContext<WCOrderAggregate>
  public readonly viewMode$: Observable<MexusViewMode>
  @Output()
  public readonly openRequested = new EventEmitter<WCOrderAggregate>()
  public readonly Unit = Unit
  private readonly translate = inject(ElderTranslateService)
  private readonly dataSource: LocalListDataSource<WCOrderAggregate>

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const viewModeService = inject(ViewModeContextService)

    this.dataSource = LocalListDataSource.from(
      [],
      'workOrderId',
      SortUtil.sortData,
      FilterUtil.filterData
    )
    this.data = DataContextBuilder.start<WCOrderAggregate>()
      .reloadOnLocalChanges()
      .build(this.dataSource)
    this.viewMode$ = viewModeService.viewMode$
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    this.data.start([new Sort('prioritySortOrder', 'asc')])
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input({ required: true })
  public set orders(orders: WCOrderAggregate[]) {
    const stateMapper = (agg: WCOrderAggregate) =>
      <WCOrderAggregate>{
        ...agg,
        state: this.resolveWorkOrderState(agg),
      }
    this.dataSource.data = [...orders].map(stateMapper)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public openWorkCenterOrder(order: WCOrderAggregate): void {
    this.openRequested.next(order)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private resolveWorkOrderState(order: WCOrderAggregate): string {
    const aggregate = order.stateAggregate
    const open = aggregate.open.itemCount
    const progress = aggregate.progress.itemCount
    const completed = aggregate.completed.itemCount
    const totalItems = open + progress + completed

    if (completed === totalItems) {
      return this.translate.instant('context.completed')
    }
    if (open === totalItems) {
      return this.translate.instant('context.open')
    }
    return this.translate.instant('processing.title')
  }
}
